/**
 * Component: VHStaffTeamManagement
 * Author: Dine AI
 * Created: December 18, 2025
 *
 * Purpose:
 * - Manages Virtual Host staff members (Magic Link users)
 * - Sends and tracks email invitations for staff
 * - Displays active staff members
 * - Handles staff removal and invitation revocation
 *
 * Staff have full operational access to VH dashboard but cannot access:
 * - Profile settings
 * - Subscription management
 * - Team management (this page - only owners can see it)
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  UserPlus,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';

interface StaffInvitation {
  id: number;
  invitation_code: string;
  staff_name: string | null;
  staff_email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

interface StaffMember {
  id: number;
  name: string | null;
  email: string;
  status: string;
  created_at: string;
  last_login_at: string | null;
  login_count: number;
}

const VHStaffTeamManagement = () => {
  const [invitations, setInvitations] = useState<StaffInvitation[]>([]);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'staff' | 'invitations'>('staff');

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<StaffInvitation | null>(null);
  const [staffToRemove, setStaffToRemove] = useState<StaffMember | null>(null);

  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [staffName, setStaffName] = useState('');
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [resending, setResending] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      console.log('[VHStaffTeamManagement] Fetching staff and invitations');
      const [invitationsRes, membersRes] = await Promise.all([
        apiClient.get('/vh-staff/invitations'),
        apiClient.get('/vh-staff/members')
      ]);

      setInvitations(Array.isArray(invitationsRes.data) ? invitationsRes.data : []);
      setStaffMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
    } catch (err) {
      console.error('Failed to fetch VH staff data:', err);
      setError('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      setError('Please enter an email address');
      return;
    }

    setError('');
    setSending(true);

    try {
      await apiClient.post('/vh-staff/invitations', {
        staff_email: inviteEmail,
        staff_name: staffName || null
      });

      setSuccess('Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteEmail('');
      setStaffName('');
      fetchData(); // Refresh list

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async () => {
    if (!invitationToRevoke) return;

    setRevoking(true);
    try {
      await apiClient.post(`/vh-staff/invitations/${invitationToRevoke.id}/revoke`);
      setSuccess('Invitation revoked');
      setShowRevokeModal(false);
      setInvitationToRevoke(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke invitation');
    } finally {
      setRevoking(false);
    }
  };

  const handleResend = async (invitation: StaffInvitation) => {
    setResending(invitation.id);
    try {
      await apiClient.post(`/vh-staff/invitations/${invitation.id}/resend`);
      setSuccess('Invitation resent');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResending(null);
    }
  };

  const handleRemoveStaff = async () => {
    if (!staffToRemove) return;

    setRemoving(true);
    try {
      await apiClient.delete(`/vh-staff/members/${staffToRemove.id}`);
      setSuccess('Staff access revoked');
      setShowRemoveModal(false);
      setStaffToRemove(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove staff');
    } finally {
      setRemoving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Pending</span>;
      case 'accepted':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Accepted</span>;
      case 'expired':
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">Expired</span>;
      case 'revoked':
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">Revoked</span>;
      case 'active':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Active</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#FDB913] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Staff Team</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Invite staff members to help manage your AI Host dashboard
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#FDB913] text-gray-900 rounded-lg font-medium hover:bg-[#e5a811] transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Invite Staff
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Tabs - Matching Order Queue style */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('staff')}
            className={`px-4 py-2.5 font-medium text-sm transition-all rounded-t-lg flex items-center gap-2 ${
              activeTab === 'staff'
                ? 'bg-[#FDB913] text-black'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Users className="h-4 w-4" />
            Active Staff
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
              activeTab === 'staff' ? 'bg-black/20 text-black' : 'bg-gray-200 text-gray-600'
            }`}>
              {staffMembers.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`px-4 py-2.5 font-medium text-sm transition-all rounded-t-lg flex items-center gap-2 ${
              activeTab === 'invitations'
                ? 'bg-[#FDB913] text-black'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Mail className="h-4 w-4" />
            Invitations
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
              activeTab === 'invitations' ? 'bg-black/20 text-black' : 'bg-orange-100 text-orange-600'
            }`}>
              {invitations.filter(i => i.status === 'pending').length}
            </span>
          </button>
        </div>
      </div>

      {/* Active Staff Tab */}
      {activeTab === 'staff' && (
        <div className="space-y-4">
          {staffMembers.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 font-medium mb-1">No Staff Members</h3>
              <p className="text-gray-500 text-sm mb-4">
                Invite staff to help manage your orders and settings
              </p>
              <button
                onClick={() => setShowInviteModal(true)}
                className="text-[#FDB913] font-medium hover:underline"
              >
                Send your first invitation
              </button>
            </div>
          ) : (
            staffMembers.map((staff) => (
              <motion.div
                key={staff.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-600 font-medium text-sm">
                        {(staff.name || staff.email).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {staff.name || 'Staff Member'}
                      </p>
                      <p className="text-sm text-gray-500">{staff.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(staff.status)}
                    <button
                      onClick={() => {
                        setStaffToRemove(staff);
                        setShowRemoveModal(true);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove access"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex gap-6 text-xs text-gray-500">
                  <span>Last login: {formatDate(staff.last_login_at)}</span>
                  <span>Logins: {staff.login_count}</span>
                  <span>Member since: {formatDate(staff.created_at)}</span>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="space-y-4">
          {invitations.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-gray-900 font-medium mb-1">No Invitations</h3>
              <p className="text-gray-500 text-sm">
                Invitations you send will appear here
              </p>
            </div>
          ) : (
            invitations.map((invitation) => (
              <motion.div
                key={invitation.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {invitation.staff_name || invitation.staff_email}
                    </p>
                    <p className="text-sm text-gray-500">{invitation.staff_email}</p>
                    <p className="text-xs text-gray-400 mt-1 font-mono">
                      {invitation.invitation_code}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(invitation.status)}
                    {invitation.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleResend(invitation)}
                          disabled={resending === invitation.id}
                          className="text-gray-400 hover:text-[#FDB913] transition-colors disabled:opacity-50"
                          title="Resend invitation"
                        >
                          {resending === invitation.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setInvitationToRevoke(invitation);
                            setShowRevokeModal(true);
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Revoke invitation"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex gap-6 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Sent: {formatDate(invitation.created_at)}
                  </span>
                  {invitation.status === 'pending' && (
                    <span>Expires: {formatDate(invitation.expires_at)}</span>
                  )}
                  {invitation.accepted_at && (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      Accepted: {formatDate(invitation.accepted_at)}
                    </span>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Invite Staff Member</h2>
            <p className="text-gray-500 text-sm mb-6">
              Send an email invitation to give someone access to your AI Host dashboard.
              They'll be able to manage orders, view payments, and adjust settings.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="staff@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={staffName}
                  onChange={(e) => setStaffName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setStaffName('');
                  setError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={sending || !inviteEmail}
                className="flex-1 px-4 py-2 bg-[#FDB913] text-gray-900 rounded-lg font-medium hover:bg-[#e5a811] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sending ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send Invitation
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {showRevokeModal && invitationToRevoke && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Revoke Invitation?</h2>
            <p className="text-gray-500 mb-6">
              This will cancel the invitation sent to{' '}
              <span className="font-medium text-gray-900">{invitationToRevoke.staff_email}</span>.
              They won't be able to use the invitation link.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setInvitationToRevoke(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={revoking}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {revoking && <RefreshCw className="h-4 w-4 animate-spin" />}
                Revoke
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Remove Staff Confirmation Modal */}
      {showRemoveModal && staffToRemove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Remove Staff Access?</h2>
            <p className="text-gray-500 mb-6">
              This will revoke dashboard access for{' '}
              <span className="font-medium text-gray-900">
                {staffToRemove.name || staffToRemove.email}
              </span>.
              They can be re-invited later if needed.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRemoveModal(false);
                  setStaffToRemove(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveStaff}
                disabled={removing}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {removing && <RefreshCw className="h-4 w-4 animate-spin" />}
                Remove Access
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default VHStaffTeamManagement;
